import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle, XCircle, Certificate, GraduationCap } from "@phosphor-icons/react";
import { downloadBlob } from "../components/hs/PdfBrandingDialog";
import { fmtDate } from "../lib/dateUtil";

function Module({ m, index, total }) {
  return (
    <article className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-3" data-testid={`course-take-module-${index}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Module {index + 1} of {total}</p>
      <h2 className="text-lg sm:text-xl font-semibold font-[Chivo] leading-tight">{m.title}</h2>
      <div className="text-sm leading-relaxed whitespace-pre-line">{m.content}</div>
      {m.key_points?.length > 0 && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 p-3">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 dark:text-amber-300 mb-1">Remember</p>
          <ul className="text-sm space-y-1 list-disc pl-4">{m.key_points.map((k, i) => <li key={i}>{k}</li>)}</ul>
        </div>
      )}
    </article>
  );
}

function Quiz({ quiz, passMark, onSubmit, busy }) {
  const [ans, setAns] = useState({});
  const done = quiz.every(q => Number.isInteger(ans[q.id]));
  return (
    <div className="space-y-3" data-testid="course-take-quiz">
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="text-lg font-semibold font-[Chivo]">Quiz</h2>
        <p className="text-xs text-muted-foreground">{quiz.length} questions · you need {passMark}% to pass. Pick one answer per question.</p>
      </div>
      {quiz.map((q, i) => (
        <div key={q.id} className="bg-card border border-border rounded-lg p-4 space-y-2" data-testid={`course-take-q-${i}`}>
          <p className="text-sm font-medium">{i + 1}. {q.question}</p>
          <div className="grid gap-1.5">
            {q.options.map((o, k) => (
              <button key={k} type="button" onClick={() => setAns({ ...ans, [q.id]: k })} aria-pressed={ans[q.id] === k} data-testid={`course-take-q-${i}-opt-${k}`}
                className={`text-left text-sm px-3 py-2.5 rounded-md border transition-colors ${ans[q.id] === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                <span className="font-semibold mr-2">{String.fromCharCode(65 + k)}</span>{o}
              </button>
            ))}
          </div>
        </div>
      ))}
      <Button size="lg" className="w-full" onClick={() => onSubmit(ans)} disabled={!done || busy} data-testid="course-take-submit">{busy ? "Marking…" : done ? "Submit answers" : `Answer all ${quiz.length} questions`}</Button>
    </div>
  );
}

function Result({ r, quiz, onRetry, onCert }) {
  return (
    <div className="space-y-3" data-testid="course-take-result">
      <div className={`rounded-lg p-5 text-center border ${r.passed ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20" : "bg-red-50 border-red-200 dark:bg-red-950/20"}`}>
        {r.passed ? <CheckCircle size={40} weight="fill" className="mx-auto text-emerald-600" /> : <XCircle size={40} weight="fill" className="mx-auto text-red-600" />}
        <p className="text-3xl font-bold font-[Chivo] mt-2" data-testid="course-take-score">{r.score}%</p>
        <p className="text-sm text-muted-foreground">{r.passed ? `Passed — pass mark ${r.pass_mark}%` : `Not quite — you need ${r.pass_mark}%. Re-read the modules, then try again.`}</p>
        {r.passed && r.certificate_no && <p className="text-xs mt-1">Certificate <span className="font-mono font-semibold">{r.certificate_no}</span>{r.expires_on ? ` · valid until ${fmtDate(r.expires_on)}` : ""} — added to your qualifications.</p>}
        <div className="flex justify-center gap-2 mt-3">
          {r.passed ? <Button onClick={onCert} data-testid="course-take-cert-btn"><Certificate size={16} className="mr-1" /> Download certificate</Button> : <Button onClick={onRetry} data-testid="course-take-retry-btn">Try again</Button>}
        </div>
      </div>
      {r.results.map((x, i) => {
        const q = quiz.find(z => z.id === x.id);
        return (
          <div key={x.id} className={`bg-card border rounded-lg p-3 text-sm ${x.correct ? "border-border" : "border-red-300"}`} data-testid={`course-take-review-${i}`}>
            <p className="font-medium">{i + 1}. {q?.question}</p>
            <p className={`text-xs mt-1 ${x.correct ? "text-emerald-700" : "text-red-700"}`}>{x.correct ? "Correct" : Number.isInteger(x.answer) ? `You answered ${x.given !== null ? String.fromCharCode(65 + x.given) : "—"} · correct answer ${String.fromCharCode(65 + x.answer)}: ${q?.options?.[x.answer]}` : `You answered ${x.given !== null ? String.fromCharCode(65 + x.given) : "—"} — re-read module${x.module ? ` ${x.module}` : "s"} and try again`}</p>
            {x.explanation && <p className="text-xs text-muted-foreground mt-1">{x.explanation}</p>}
          </div>
        );
      })}
    </div>
  );
}

/** Phone-first course player: read each module, then the quiz; a pass issues a certificate on the spot. */
export default function CourseTakePage() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => API.get(`/in-house-portal/training/${id}`).then(({ data }) => setA(data)).catch(e => toast.error(formatApiError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (a && a.status === "assigned") API.post(`/in-house-portal/training/${id}/start`).catch(() => {}); }, [a, id]);

  if (!a) return <div className="p-6 text-sm text-muted-foreground" data-testid="course-take-loading">Loading course…</div>;
  const c = a.course;
  const modules = c.modules || [];
  const total = modules.length + 1;
  const atQuiz = step >= modules.length;

  const submit = async (answers) => {
    setBusy(true);
    try { const { data } = await API.post(`/in-house-portal/training/${id}/submit`, { answers }); setResult(data); window.scrollTo({ top: 0, behavior: "smooth" }); if (data.passed) load(); } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const cert = async () => { try { const resp = await API.get(`/in-house-portal/training/${id}/certificate.pdf`, { responseType: "blob" }); downloadBlob(resp.data, `${result?.certificate_no || a.certificate_no || "certificate"}.pdf`); } catch { toast.error("Certificate failed"); } };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-4 pb-36 md:pb-6" data-testid="course-take-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/my-work" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="course-take-back"><ArrowLeft size={14} /> My Work</Link>
        <div className="flex-1" />
        {a.status === "passed" && <Badge className="bg-emerald-100 text-emerald-800" data-testid="course-take-passed-badge">Passed · {a.certificate_no}</Badge>}
      </div>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><GraduationCap size={22} weight="bold" /></div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] leading-tight" data-testid="course-take-title">{c.title}</h1>
          <p className="text-xs text-muted-foreground">{c.blurb} · about {c.duration_minutes} min{a.due_date ? ` · complete by ${fmtDate(a.due_date)}` : ""}</p>
        </div>
      </div>

      {a.status === "passed" && !result ? (
        <div className="bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/20 rounded-lg p-5 text-center space-y-2" data-testid="course-take-already-passed">
          <CheckCircle size={36} weight="fill" className="mx-auto text-emerald-600" />
          <p className="text-sm">You passed this course on {fmtDate(a.passed_at)} with {a.score ?? a.best_score}%.{a.expires_on ? ` Valid until ${fmtDate(a.expires_on)}.` : ""}</p>
          <Button onClick={cert} data-testid="course-take-cert-btn"><Certificate size={16} className="mr-1" /> Download certificate</Button>
          <div className="pt-2"><button type="button" className="text-xs text-primary hover:underline" onClick={() => setStep(0)} data-testid="course-take-reread">Re-read the modules</button></div>
          {step < modules.length && modules.length > 0 && <div className="text-left pt-3"><Module m={modules[step]} index={step} total={modules.length} /><div className="flex justify-between mt-2"><Button variant="outline" size="sm" disabled={step === 0} onClick={() => setStep(step - 1)}>Back</Button><Button size="sm" disabled={step >= modules.length - 1} onClick={() => setStep(step + 1)}>Next</Button></div></div>}
        </div>
      ) : result ? (
        <Result r={result} quiz={c.quiz} onRetry={() => { setResult(null); setStep(modules.length); window.scrollTo(0, 0); }} onCert={cert} />
      ) : (
        <>
          <div className="flex items-center gap-1" data-testid="course-take-progress">
            {Array.from({ length: total }).map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-muted"}`} />)}
          </div>
          {!atQuiz ? <Module m={modules[step]} index={step} total={modules.length} /> : <Quiz quiz={c.quiz} passMark={c.pass_mark} onSubmit={submit} busy={busy} />}
          {!atQuiz && (
            <div className="flex items-center justify-between gap-2">
              <Button variant="outline" disabled={step === 0} onClick={() => { setStep(step - 1); window.scrollTo(0, 0); }} data-testid="course-take-prev"><ArrowLeft size={14} className="mr-1" /> Back</Button>
              <Button onClick={() => { setStep(step + 1); window.scrollTo(0, 0); }} data-testid="course-take-next">{step === modules.length - 1 ? "Start the quiz" : "Next module"} <ArrowRight size={14} className="ml-1" /></Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
